import { MediaRowBase } from '@components/common/MediaRowBase/MediaRowBase';
import baseCss from '@components/common/MediaRowBase/MediaRowBase.module.scss';
import { DownloadIcon } from '@components/Icons/Download';
import GiftIcon from '@components/Icons/GiftIcon';
import { PDFAdobeIcon } from '@components/Icons/PDFAdobeIcon';
import { formatCredits, formatDate } from '@lib/utils/units';
import { billingStore } from '@stores/Billing';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import React from 'react';
import css from './InvoiceItemRow.module.scss';

export interface InvoiceItem {
  id: string;
  invoiceName: string;
  invoiceNumber: string;
  creditsObtained: number;
  creditsUsed: number;
  transactionDate: number;
  expiryDate: number;
  type: 'subscription' | 'one-time-purchase' | 'signup';
}

interface InvoiceItemRowProps {
  invoice: InvoiceItem;
  isSelected: boolean;
  onToggleSelection: (invoiceId: string) => void;
  showCheckbox?: boolean;
  height?: number;
  start?: number;
}

export const InvoiceItemRow = React.forwardRef<HTMLDivElement, InvoiceItemRowProps>(
  ({ invoice, isSelected, onToggleSelection, showCheckbox = true, height, start }, ref) => {
    const handleToggleSelection = () => {
      onToggleSelection(invoice.id);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggleSelection();
      }
    };

    const handleDownload = async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        trackingStore.invoiceDownloaded({ invoiceId: invoice.id });
        const invoiceUrl = await billingStore.getInvoiceUrl(invoice.id);
        if (invoiceUrl) {
          window.open(invoiceUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening invoice:', error);
      }
    };

    const rightContent = (
      <>
        <div className={classNames(baseCss.thumbnailContainer, css.invoiceInfo)}>
          <div className={css.pdfIcon}>
            {invoice.type === 'signup' ? <GiftIcon /> : <PDFAdobeIcon />}
          </div>
          <div className={css.invoiceDetails}>
            <span className={classNames(baseCss.title, css.invoiceNumber)}>
              {invoice.type === 'signup' ? 'Signup Bonus' : invoice.invoiceName}
            </span>
            {invoice.invoiceNumber && invoice.invoiceNumber.trim() && (
              <button
                className={css.downloadButton}
                onClick={handleDownload}
                title="Open invoice in new tab"
              >
                <DownloadIcon />
              </button>
            )}
          </div>
        </div>
        <span className={classNames(css.creditsObtained, baseCss.itemDataCell)}>
          {formatCredits(invoice.creditsObtained)}
        </span>
        <span className={classNames(css.creditsUsed, baseCss.itemDataCell)}>
          {formatCredits(invoice.creditsUsed)}
        </span>
        <span className={classNames(css.transactionDate, baseCss.itemDataCell)}>
          {formatDate(invoice.transactionDate)}
        </span>
        <span className={classNames(css.expiryDate, baseCss.itemDataCell)}>
          {formatDate(invoice.expiryDate)}
        </span>
        <span className={classNames(css.type, baseCss.itemDataCell)}>
          {invoice.type.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
        </span>
      </>
    );

    return (
      <MediaRowBase
        ref={ref}
        variant="item"
        tableTheme="invoice"
        className={classNames({
          [css.virtualRow]: height !== undefined && start !== undefined,
        })}
        showCheckbox={false}
        checkboxContent={null}
        rightContent={rightContent}
        onClick={handleToggleSelection}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        style={
          height !== undefined && start !== undefined
            ? {
                height: `${height - 8}px`,
                transform: `translateY(${start + 4}px)`,
              }
            : undefined
        }
      />
    );
  },
);

InvoiceItemRow.displayName = 'InvoiceItemRow';
